import { Printer, Wand2 } from "lucide-react";
import {
  required,
  useDataProvider,
  useGetList,
  useGetOne,
  useRecordContext,
  useTranslate,
} from "ra-core";
import { useFormContext, useWatch } from "react-hook-form";
import { Link, useParams } from "react-router";
import { ArrayInput } from "@/components/admin/array-input";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { CreateButton } from "@/components/admin/create-button";
import { Create } from "@/components/admin/create";
import { DataTable } from "@/components/admin/data-table";
import { DateInput } from "@/components/admin/date-input";
import { Edit } from "@/components/admin/edit";
import { List } from "@/components/admin/list";
import { MobileRecordCard } from "@/components/admin/mobile-record-card";
import { NumberInput } from "@/components/admin/number-input";
import { ReferenceField } from "@/components/admin/reference-field";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { SimpleForm } from "@/components/admin/simple-form";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";
import { TextInput } from "@/components/admin/text-input";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { TopToolbar } from "../../atomic-crm/layout/TopToolbar";
import { useConfigurationContext } from "../../atomic-crm/root/ConfigurationContext";
import { findChoice, QUOTE_STATUSES } from "../common/constants";
import {
  StatusBadge,
  todayIso,
  useFormatDate,
  useFormatMoney,
} from "../common/components";
import type { DealMilestone, QuoteItem } from "../types";

export const quoteTotals = (items: QuoteItem[] = [], taxRate = 21) => {
  const subtotal = items.reduce(
    (sum, item) =>
      sum + Number(item.quantity || 0) * Number(item.unit_price || 0),
    0,
  );
  const tax = (subtotal * Number(taxRate || 0)) / 100;
  return { subtotal, tax, total: subtotal + tax };
};

/** Next document number of the year: P-2026-001, F-2026-002… */
export const useNextNumber = (resource: string, prefix: string) => {
  const year = new Date().getFullYear();
  const { total } = useGetList(resource, {
    filter: { "number@ilike": `${prefix}-${year}-` },
    pagination: { page: 1, perPage: 1 },
  });
  return `${prefix}-${year}-${String((total ?? 0) + 1).padStart(3, "0")}`;
};

const QuoteList = () => {
  const formatDate = useFormatDate();
  const formatMoney = useFormatMoney();
  return (
    <List
      resource="quotes"
      sort={{ field: "issue_date", order: "DESC" }}
      actions={
        <TopToolbar>
          <CreateButton label="hub.quotes.new" />
        </TopToolbar>
      }
    >
      <DataTable
        rowClick="edit"
        bulkActionButtons={false}
        mobileCard={(q) => (
          <MobileRecordCard
            title={q.title || q.number}
            trailing={
              <StatusBadge choice={findChoice(QUOTE_STATUSES, q.status)} />
            }
            subtitle={
              q.company_id != null ? (
                <ReferenceField
                  source="company_id"
                  reference="companies"
                  link={false}
                />
              ) : null
            }
            meta={[
              q.title ? q.number : null,
              <span className="font-medium text-foreground">
                {formatMoney(quoteTotals(q.items, q.tax_rate).total)}
              </span>,
              formatDate(q.issue_date),
            ]}
          />
        )}
      >
        <DataTable.Col source="number" />
        <DataTable.Col source="title" />
        <DataTable.Col source="company_id">
          <ReferenceField
            source="company_id"
            reference="companies"
            link="show"
          />
        </DataTable.Col>
        <DataTable.Col
          source="issue_date"
          render={(q) => formatDate(q.issue_date)}
        />
        <DataTable.Col
          source="items"
          label="hub.quotes.total"
          render={(q) => formatMoney(quoteTotals(q.items, q.tax_rate).total)}
        />
        <DataTable.Col
          source="status"
          render={(q) => (
            <StatusBadge choice={findChoice(QUOTE_STATUSES, q.status)} />
          )}
        />
      </DataTable>
    </List>
  );
};

const LiveTotals = () => {
  const items = useWatch({ name: "items" }) as QuoteItem[] | undefined;
  const taxRate = useWatch({ name: "tax_rate" }) as number | undefined;
  const translate = useTranslate();
  const formatMoney = useFormatMoney();
  const { subtotal, tax, total } = quoteTotals(items, taxRate);
  return (
    <div className="ml-auto w-64 space-y-1 text-sm">
      <p className="flex justify-between">
        <span>{translate("hub.quotes.subtotal")}</span>
        {formatMoney(subtotal)}
      </p>
      <p className="flex justify-between text-muted-foreground">
        <span>
          {translate("hub.quotes.tax")} ({taxRate ?? 0}%)
        </span>
        {formatMoney(tax)}
      </p>
      <p className="flex justify-between border-t pt-1 font-semibold">
        <span>{translate("hub.quotes.total")}</span>
        {formatMoney(total)}
      </p>
    </div>
  );
};

/** Fills the items with the milestones (and amounts) of the linked deal. */
const FillFromMilestones = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const { setValue } = useFormContext();
  const dealId = useWatch({ name: "deal_id" });
  if (dealId == null || dealId === "") return null;
  const fill = async () => {
    const { data } = await dataProvider.getList<DealMilestone>(
      "deal_milestones",
      {
        filter: { deal_id: dealId },
        sort: { field: "index", order: "ASC" },
        pagination: { page: 1, perPage: 100 },
      },
    );
    setValue(
      "items",
      data.map((m) => ({
        description: m.title,
        quantity: 1,
        unit_price: Number(m.amount ?? 0),
      })),
      { shouldDirty: true },
    );
  };
  return (
    <Button type="button" variant="outline" size="sm" onClick={fill}>
      <Wand2 className="size-4" />
      {translate("hub.quotes.from_milestones")}
    </Button>
  );
};

const QuoteInputs = () => (
  <div className="flex flex-col gap-4">
    <div className="grid gap-4 sm:grid-cols-[160px_1fr_180px] [&>*]:min-w-0 [&>*>*]:min-w-0">
      <TextInput source="number" validate={required()} />
      <TextInput source="title" validate={required()} />
      <SelectInput
        source="status"
        choices={QUOTE_STATUSES}
        validate={required()}
      />
    </div>
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4 [&>*]:min-w-0 [&>*>*]:min-w-0">
      <ReferenceInput source="company_id" reference="companies">
        <AutocompleteInput
          label="resources.quotes.fields.company_id"
          optionText="name"
        />
      </ReferenceInput>
      <ReferenceInput source="deal_id" reference="deals">
        <AutocompleteInput
          label="resources.quotes.fields.deal_id"
          optionText="name"
        />
      </ReferenceInput>
      <DateInput source="issue_date" validate={required()} />
      <DateInput source="valid_until" />
    </div>
    <div className="flex justify-end">
      <FillFromMilestones />
    </div>
    <ArrayInput source="items">
      <SimpleFormIterator inline>
        <TextInput
          source="description"
          label="resources.quotes.fields.description"
          className="min-w-64 grow"
        />
        <NumberInput
          source="quantity"
          label="resources.quotes.fields.quantity"
          min={0}
          step={1}
          className="w-24"
        />
        <NumberInput
          source="unit_price"
          label="resources.quotes.fields.unit_price"
          min={0}
          step={0.01}
          className="w-36"
        />
      </SimpleFormIterator>
    </ArrayInput>
    <div className="flex items-start gap-4">
      <NumberInput source="tax_rate" min={0} max={100} className="w-32" />
      <LiveTotals />
    </div>
    <TextInput source="terms" multiline rows={3} />
    <TextInput source="notes" multiline rows={2} />
  </div>
);

const QuoteCreate = () => {
  const number = useNextNumber("quotes", "P");
  return (
    <Create
      resource="quotes"
      redirect="edit"
      className="mx-auto w-full max-w-5xl"
    >
      <Card>
        <CardContent>
          <SimpleForm
            className="max-w-none"
            defaultValues={{
              number,
              status: "draft",
              issue_date: todayIso(),
              tax_rate: 21,
              items: [{ description: "", quantity: 1, unit_price: 0 }],
            }}
          >
            <QuoteInputs />
          </SimpleForm>
        </CardContent>
      </Card>
    </Create>
  );
};

const PrintLink = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record) return null;
  return (
    <TopToolbar>
      <Link
        to={`/quotes/${record.id}/print`}
        target="_blank"
        className={buttonVariants({ variant: "outline", size: "sm" })}
      >
        <Printer className="size-4" />
        {translate("hub.quotes.print")}
      </Link>
    </TopToolbar>
  );
};

const QuoteEdit = () => (
  <Edit
    resource="quotes"
    redirect={false}
    mutationMode="pessimistic"
    actions={<PrintLink />}
    className="mx-auto w-full max-w-5xl"
  >
    <Card>
      <CardContent>
        <SimpleForm className="max-w-none">
          <QuoteInputs />
        </SimpleForm>
      </CardContent>
    </Card>
  </Edit>
);

/** Printable quote (no app chrome): the browser's "Save as PDF" makes the PDF. */
export const QuotePrint = () => {
  const { id } = useParams();
  const translate = useTranslate();
  const formatMoney = useFormatMoney();
  const formatDate = useFormatDate();
  const { lightModeLogo } = useConfigurationContext();
  const { data: quote } = useGetOne("quotes", { id: id! });
  const { data: company } = useGetOne(
    "companies",
    { id: quote?.company_id },
    { enabled: quote?.company_id != null },
  );
  if (!quote) return null;
  const { subtotal, tax, total } = quoteTotals(quote.items, quote.tax_rate);

  return (
    <div className="min-h-screen bg-white text-black">
      <div className="mx-auto max-w-3xl space-y-8 p-10 print:p-0">
        <div className="flex justify-end print:hidden">
          <Button onClick={() => window.print()}>
            <Printer className="size-4" />
            {translate("hub.quotes.print")}
          </Button>
        </div>
        <header className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <img src={lightModeLogo} alt="Litus Digital" className="size-12" />
            <div>
              <p className="text-xl font-semibold">Litus Digital</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-semibold uppercase tracking-wide">
              {translate("hub.quotes.quote")}
            </p>
            <p className="text-sm">{quote.number}</p>
            <p className="text-sm">{formatDate(quote.issue_date)}</p>
            {quote.valid_until && (
              <p className="text-sm text-gray-600">
                {translate("hub.quotes.valid_until", {
                  date: formatDate(quote.valid_until),
                })}
              </p>
            )}
          </div>
        </header>
        {company && (
          <div className="text-sm">
            <p className="font-semibold">{company.name}</p>
            {company.tax_identifier && <p>{company.tax_identifier}</p>}
            {company.address && <p>{company.address}</p>}
            {(company.zipcode || company.city) && (
              <p>
                {company.zipcode} {company.city}
              </p>
            )}
          </div>
        )}
        <h1 className="text-xl font-semibold">{quote.title}</h1>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-black text-left">
              <th className="py-2">
                {translate("resources.quotes.fields.description")}
              </th>
              <th className="py-2 text-right">
                {translate("resources.quotes.fields.quantity")}
              </th>
              <th className="py-2 text-right">
                {translate("resources.quotes.fields.unit_price")}
              </th>
              <th className="py-2 text-right">
                {translate("hub.quotes.total")}
              </th>
            </tr>
          </thead>
          <tbody>
            {(quote.items as QuoteItem[]).map((item, i) => (
              <tr key={i} className="border-b border-gray-200">
                <td className="py-2">{item.description}</td>
                <td className="py-2 text-right">{item.quantity}</td>
                <td className="py-2 text-right">
                  {formatMoney(item.unit_price)}
                </td>
                <td className="py-2 text-right">
                  {formatMoney(Number(item.quantity) * Number(item.unit_price))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="ml-auto w-64 space-y-1 text-sm">
          <p className="flex justify-between">
            <span>{translate("hub.quotes.subtotal")}</span>
            {formatMoney(subtotal)}
          </p>
          <p className="flex justify-between">
            <span>
              {translate("hub.quotes.tax")} ({quote.tax_rate}%)
            </span>
            {formatMoney(tax)}
          </p>
          <p className="flex justify-between border-t-2 border-black pt-1 text-base font-semibold">
            <span>{translate("hub.quotes.total")}</span>
            {formatMoney(total)}
          </p>
        </div>
        {quote.terms && (
          <div className="text-sm">
            <p className="font-semibold">
              {translate("resources.quotes.fields.terms")}
            </p>
            <p className="whitespace-pre-line">{quote.terms}</p>
          </div>
        )}
        {quote.notes && (
          <p className="whitespace-pre-line text-sm text-gray-600">
            {quote.notes}
          </p>
        )}
      </div>
    </div>
  );
};

QuotePrint.path = "/quotes/:id/print";

export default {
  list: QuoteList,
  create: QuoteCreate,
  edit: QuoteEdit,
  recordRepresentation: "number",
};
